import React, { useMemo, useState } from "react";

import { ResponsiveLine } from "@nivo/line";

import { compareAsc, format } from "date-fns";
import useTranslations from "../../hooks/useTranslations";

import {
  MadFullFragment,
  ReportAnswerFragment,
  ReportFragment,
  TemplateQuestionFragment,
  UserSummaryFragment,
} from "../../graphql/fragments";
import { ReportAnswerType } from "../../graphql/types";

import useGetReports from "../../graphql/hooks/queries/useGetReports";

import { Badge, Checkbox, H4 } from "../wombat-ui";
import { orderBy } from "lodash";
import { StarIcon } from "../icons";

function isTimeSpentOvertime(
  hour: number,
  expectedValue: number,
  expectedUnit: string
): boolean {
  let multiplier = 1;
  switch (expectedUnit) {
    case "minutes":
      multiplier = 0.0166667;
      break;
    case "hours":
      multiplier = 1;
      break;
    case "days":
      multiplier = 24;
      break;
    case "months":
      multiplier = 30 * 24;
      break;
    case "years":
      multiplier = 365 * 24;
      break;
  }

  return hour > expectedValue * multiplier;
}

function getDataFromReports(
  talent: UserSummaryFragment,
  referents: UserSummaryFragment[],
  reports: ReportFragment[]
) {
  const data = {
    evaluations: [],
    skills: [],
  };
  reports.map(({ reportAnswers, checkedAt }) => {
    reportAnswers.map(
      ({ type, value, user: { id: userId }, templateQuestion }) => {
        if (type === "ReportAnswerEvaluation") {
          let evaluationMapIndex = data.evaluations.findIndex(
            (i) => i?.digest === templateQuestion.digest
          );
          const goal = parseInt(
            templateQuestion.options.find((o) => o.isGoal === true)?.value ||
              "-1",
            10
          );

          if (evaluationMapIndex < 0)
            evaluationMapIndex =
              data.evaluations.push({
                digest: templateQuestion.digest,
                templateQuestion,
                goal: goal != -1 ? goal : undefined,
                data: [
                  {
                    id: talent.id,
                    color: "#81A1C1",
                    name: talent.name,
                    data: [],
                  },
                  ...referents.map((r) => ({
                    id: r.id,
                    color: "#EBCB8B",
                    name: r.name,
                    data: [],
                  })),
                ],
              }) - 1;

          const userIdx = data.evaluations[evaluationMapIndex].data.findIndex(
            (d) => d.id === userId
          );

          data.evaluations[evaluationMapIndex].data[userIdx].data.push({
            x: format(new Date(checkedAt), "yyyy-MM-dd"),
            y: parseInt(value.selector as string, 10),
          });
        } else if (type === "ReportAnswerSkill") {
          let skillMapIndex = data.skills.findIndex(
            (i) => i?.digest === templateQuestion.digest
          );
          const goal = parseInt(
            templateQuestion.options.find((o) => o.isGoal === true)?.value ||
              "-1",
            10
          );
          if (skillMapIndex < 0)
            skillMapIndex =
              data.skills.push({
                digest: templateQuestion.digest,
                templateQuestion,
                goal: goal != -1 ? goal : undefined,
                data: [
                  {
                    id: talent.id,
                    color: "#81A1C1",
                    name: talent.name,
                    data: [],
                  },
                  ...referents.map((r) => ({
                    id: r.id,
                    color: "#EBCB8B",
                    name: r.name,
                    data: [],
                  })),
                ],
              }) - 1;
          const gradeValue = parseInt(value.selector as string, 10);
          const dataPoint = {
            x: format(new Date(checkedAt), "yyyy-MM-dd"),
            y: gradeValue,
          };
          const userIdx = data.skills[skillMapIndex].data.findIndex(
            (d) => d.id === userId
          );

          data.skills[skillMapIndex].data[userIdx].data.push(dataPoint);
        }
      }
    );
  });

  data.skills.forEach((skill, idx) => {
    skill.data.forEach((d, innerIdx) => {
      data.skills[idx].data[innerIdx] = {
        ...data.skills[idx].data[innerIdx],
        data: d.data.sort((i, j) => compareAsc(new Date(i.x), new Date(j.x))),
      };
    });
  });
  data.evaluations.forEach((skill, idx) => {
    skill.data.forEach((d, innerIdx) => {
      data.evaluations[idx].data[innerIdx] = {
        ...data.evaluations[idx].data[innerIdx],
        data: d.data.sort((i, j) => compareAsc(new Date(i.x), new Date(j.x))),
      };
    });
  });
  return data;
}

function getTickLabel(
  value: number,
  templateQuestion: TemplateQuestionFragment
): string {
  return templateQuestion?.options?.[value]?.label;
}

type UserFollowUpAnalyticsProps = {
  mad: MadFullFragment;
};

const UserFollowUpAnalytics: React.FC<UserFollowUpAnalyticsProps> = ({
  mad,
}) => {
  const { data, loading } = useGetReports({
    variables: { madId: mad.id, page: 1, per: 30 },
  });
  const t = useTranslations();

  const chartData = useMemo(
    () =>
      getDataFromReports(
        mad.talent,
        mad.referents,
        data?.reports?.collection || []
      ),
    [data]
  );

  const [
    selectedReportAnswer,
    setSelectedReportAnswer,
  ] = useState<ReportAnswerFragment>(undefined);
  const [selectedData, setSelectedData] = useState<
    Extract<ReportAnswerType, "ReportAnswerEvaluation" | "ReportAnswerSkill">
  >("ReportAnswerSkill");

  const reportAnswers = useMemo((): {
    skills: ReportAnswerFragment[];
    evaluations: ReportAnswerFragment[];
  } => {
    const reportAnswers =
      data?.reports?.collection.flatMap(({ reportAnswers }) => reportAnswers) ||
      [];

    const _answers: {
      skills: ReportAnswerFragment[];
      evaluations: ReportAnswerFragment[];
    } = {
      skills: [],
      evaluations: [],
    };

    reportAnswers.map((rA) => {
      if (
        rA.type === "ReportAnswerSkill" &&
        !_answers.skills.find(
          (a) => a.templateQuestion.digest === rA.templateQuestion.digest
        )
      ) {
        _answers.skills.push(rA);
      } else if (
        rA.type === "ReportAnswerEvaluation" &&
        !_answers.evaluations.find(
          (a) => a.templateQuestion.digest === rA.templateQuestion.digest
        )
      ) {
        _answers.evaluations.push(rA);
      }
    });

    setSelectedReportAnswer(
      _answers.skills.length ? _answers.skills?.[0] : _answers.evaluations?.[0]
    );
    setSelectedData(
      _answers.skills.length ? "ReportAnswerSkill" : "ReportAnswerEvaluation"
    );
    return _answers;
  }, [data]);

  const [selectedChartUsers, setSelectedChartUsers] = useState<
    { id: string; name: string }[]
  >([]);

  const selectedChartData = useMemo(() => {
    const data = chartData[
      selectedData === "ReportAnswerSkill" ? "skills" : "evaluations"
    ].find(
      (skill) => selectedReportAnswer?.templateQuestion.digest === skill.digest
    );

    setSelectedChartUsers(data?.data.map((d) => ({ id: d.id, name: d.name })));

    return data;
  }, [selectedReportAnswer]);

  const timeSpentLearning = useMemo(() => {
    const rAs =
      data?.reports?.collection.flatMap(({ reportAnswers }) => reportAnswers) ||
      [];

    const timeSpentMap: Record<
      string,
      {
        templateQuestion: TemplateQuestionFragment;
        timeSpent: number;
        expectedTime: number;
        expectedTimeUnit: string;
        referentCount: number;
      }
    > = {};

    rAs.forEach((ra) => {
      if (
        ra.templateQuestion.timeTracked &&
        !timeSpentMap[ra.templateQuestion.digest] &&
        mad.referents.map((r) => r.id).includes(ra.user.id)
      )
        timeSpentMap[ra.templateQuestion.digest] = {
          templateQuestion: ra.templateQuestion,
          timeSpent: ra.timeSpent,
          expectedTime: ra.templateQuestion.expectedTimeValue,
          expectedTimeUnit: ra.templateQuestion.expectedTimeUnit,
          referentCount: 1,
        };
      else if (
        ra.templateQuestion.timeTracked &&
        timeSpentMap[ra.templateQuestion.digest] &&
        mad.referents.map((r) => r.id).includes(ra.user.id)
      ) {
        timeSpentMap[ra.templateQuestion.digest].timeSpent += ra.timeSpent;
        timeSpentMap[ra.templateQuestion.digest].referentCount += 1;
      }
    });
    return timeSpentMap;
  }, [data]);

  return reportAnswers.skills.length === 0 &&
    reportAnswers.evaluations.length === 0 ? (
    <div className="flex justify-center text-sm text-light-900">
      {t("mads.noSkillsTracked")}
    </div>
  ) : (
    <div className="w-full overflow-auto min-w-xl">
      <ul className="grid grid-cols-1 gap-3 mt-3 sm:gap-4 sm:grid-cols-4">
        {orderBy(
          reportAnswers.skills,
          "templateQuestion.subject.name",
          "asc"
        ).map((skill) => {
          const chartSkill = chartData.skills.find(
            (s) => skill.templateQuestion.digest === s.digest
          );

          const hasGoal = chartSkill?.goal > 0;
          const timeSpent = timeSpentLearning[skill.templateQuestion.digest];

          return (
            <li
              className="flex col-span-1 rounded-md shadow-sm group"
              key={skill.id}
              role="button"
              onClick={() => {
                setSelectedData("ReportAnswerSkill");
                setSelectedReportAnswer(skill);
              }}
            >
              <div
                className={`flex items-center justify-center flex-shrink-0 w-1.5 text-sm font-medium text-white ${
                  selectedReportAnswer?.templateQuestion?.digest ===
                  skill.templateQuestion.digest
                    ? "bg-primary-600"
                    : "bg-primary-400 group-hover:bg-primary-600"
                } rounded-l-md`}
              ></div>
              <div className="flex items-center justify-between flex-1 truncate bg-white border-t border-b border-r border-gray-200 rounded-r-md">
                <div className="flex-1 px-4 py-2 text-sm truncate">
                  <span className="font-medium text-dark-100 hover:text-dark-300">
                    {skill.templateQuestion.subject.name}
                    {timeSpent && (
                      <span className="ml-1 text-light-900">
                        -{" "}
                        {t("mads.timeSpentShort", {
                          timeSpentAverage: Math.round(
                            timeSpent.timeSpent / timeSpent.referentCount
                          ),
                        })}
                      </span>
                    )}
                  </span>
                </div>
                {hasGoal && (
                  <div className="flex-shrink-0 pr-2">
                    <button className="inline-flex items-center justify-center w-8 h-8 bg-transparent bg-white rounded-full text-light-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                      <span className="sr-only">
                        {skill.value >= chartSkill.goal
                          ? t("mads.goalReached")
                          : t("mads.goalBeingReached")}
                      </span>
                      <StarIcon
                        iconClassName={`w-5 h-5 ${
                          skill.value >= chartSkill.goal
                            ? "text-warning-500"
                            : ""
                        }`}
                      />
                    </button>
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      <ul className="grid grid-cols-1 gap-3 mt-3 sm:gap-4 sm:grid-cols-4">
        {orderBy(
          reportAnswers.evaluations,
          "templateQuestion.label",
          "asc"
        ).map((evaluation) => {
          const chartEvaluation = chartData.evaluations.find(
            (s) => evaluation.templateQuestion.digest === s.digest
          );

          const hasGoal = chartEvaluation?.goal > 0;
          const timeSpent =
            timeSpentLearning[evaluation.templateQuestion.digest];

          return (
            <li
              className="flex col-span-1 rounded-md shadow-sm group"
              key={evaluation.id}
              role="button"
              onClick={() => {
                setSelectedData("ReportAnswerEvaluation");
                setSelectedReportAnswer(evaluation);
              }}
            >
              <div
                className={`flex items-center justify-center flex-shrink-0 w-1.5 text-sm font-medium text-white ${
                  selectedReportAnswer?.id === evaluation.id
                    ? "bg-accent-500"
                    : "bg-accent-300 group-hover:bg-accent-700"
                } rounded-l-md`}
              ></div>
              <div className="flex items-center justify-between flex-1 truncate bg-white border-t border-b border-r border-gray-200 rounded-r-md">
                <div className="flex-1 px-4 py-2 text-sm truncate">
                  <span className="font-medium text-dark-100 hover:text-dark-300">
                    {evaluation.templateQuestion.label}
                    {timeSpent && (
                      <span className="ml-1 text-light-900">
                        -{" "}
                        {t("mads.timeSpentShort", {
                          timeSpentAverage: Math.round(
                            timeSpent.timeSpent / timeSpent.referentCount
                          ),
                        })}
                      </span>
                    )}
                  </span>
                </div>
                {hasGoal && (
                  <div className="flex-shrink-0 pr-2">
                    <button className="inline-flex items-center justify-center w-8 h-8 bg-transparent bg-white rounded-full text-light-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                      <span className="sr-only">
                        {evaluation.value >= chartEvaluation.goal
                          ? t("mads.goalReached")
                          : t("mads.goalBeingReached")}
                      </span>
                      <StarIcon
                        iconClassName={`w-5 h-5 ${
                          evaluation.value >= chartEvaluation.goal
                            ? "text-warning-500"
                            : ""
                        }`}
                      />
                    </button>
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {!loading &&
        (reportAnswers.skills.length > 0 ||
          reportAnswers.evaluations.length > 0) && (
          <div className="flex pt-8">
            <div className="flex-col flex-grow">
              <H4>
                {selectedData === "ReportAnswerSkill"
                  ? selectedReportAnswer?.templateQuestion?.subject.name
                  : selectedReportAnswer?.templateQuestion?.label}
                {timeSpentLearning[
                  selectedReportAnswer?.templateQuestion.digest
                ] && (
                  <span className="ml-1 text-light-900">
                    -{" "}
                    <span
                      className={`${
                        isTimeSpentOvertime(
                          timeSpentLearning[
                            selectedReportAnswer?.templateQuestion.digest
                          ].timeSpent /
                            timeSpentLearning[
                              selectedReportAnswer?.templateQuestion.digest
                            ].referentCount,
                          timeSpentLearning[
                            selectedReportAnswer?.templateQuestion.digest
                          ].expectedTime,
                          timeSpentLearning[
                            selectedReportAnswer?.templateQuestion.digest
                          ].expectedTimeUnit
                        )
                          ? "text-danger-500"
                          : ""
                      }`}
                    >
                      {t("mads.timeSpentShort", {
                        timeSpentAverage:
                          Math.round(timeSpentLearning[
                            selectedReportAnswer?.templateQuestion.digest
                          ].timeSpent /
                          timeSpentLearning[
                            selectedReportAnswer?.templateQuestion.digest
                          ].referentCount),
                      })}
                      {timeSpentLearning[
                        selectedReportAnswer?.templateQuestion.digest
                      ].expectedTime &&
                        ` (${t(`reports.expectedTime.hours`, {
                          count:
                            timeSpentLearning[
                              selectedReportAnswer?.templateQuestion.digest
                            ].expectedTime,
                        })})`}
                    </span>
                  </span>
                )}
              </H4>
              <div className="flex-grow bg-white h-96 text-dark-100">
                <ResponsiveLine
                  margin={{ top: 20, right: 80, bottom: 60, left: 80 }}
                  animate
                  enableArea
                  enableSlices="x"
                  colors={(d) => d.color}
                  pointSize={8}
                  pointBorderColor="#fff"
                  pointBorderWidth={2}
                  data={
                    selectedChartData?.data
                      ? selectedChartData.data.filter((d) =>
                          selectedChartUsers?.find((u) => u.id === d.id)
                        )
                      : []
                  }
                  markers={
                    selectedChartData?.goal !== undefined
                      ? [
                          {
                            axis: "y",
                            value: selectedChartData?.goal,
                            lineStyle: { stroke: "#F00", strokeWidth: 2 },
                            legend: t("mads.skillGoal") as string,
                            textStyle: {
                              fontSize: "0.875rem",
                              lineHeight: "1.25rem",
                              color:
                                "rgba(76, 86, 106, var(--tw-text-opacity))",
                            },
                          },
                        ]
                      : undefined
                  }
                  xScale={{
                    type: "time",
                    format: "%Y-%m-%d",
                    useUTC: false,
                    precision: "month",
                  }}
                  xFormat="time:%d-%m-%Y"
                  yScale={{
                    type: "linear",
                    min: 0,
                    max: 4,
                  }}
                  axisLeft={{
                    format: (value) => {
                      return getTickLabel(
                        value as number,
                        selectedReportAnswer?.templateQuestion
                      );
                    },
                    legendOffset: 12,
                  }}
                  axisBottom={{
                    format: "%b %d",
                    tickRotation: 45,
                  }}
                  sliceTooltip={({ slice }) => {
                    return (
                      <div className="rounded-md shadow-md bg-dark-100 text-light-0">
                        {slice.points.map((point, index) => {
                          const user = point.id.includes(mad.talent.id)
                            ? mad.talent
                            : mad.referents.find((r) =>
                                point.id.includes(r.id)
                              );

                          return (
                            <div className="pb-4" key={point.id}>
                              {index === 0 && (
                                <div className="px-5 py-4 mb-4 bg-dark-300 rounded-t-md">
                                  <h5 className="text-light-300">
                                    {t("mads.reportDated")}{" "}
                                    {point.data.xFormatted}
                                  </h5>
                                  <p className="text-sm text-light-500">
                                    {slice.points.length} notations
                                  </p>
                                </div>
                              )}
                              {!point.id.includes("underGoal") && (
                                <div
                                  className={`flex items-center px-5 ${
                                    index === 0 ? "" : "pt-3"
                                  }`}
                                >
                                  <div
                                    className="w-2.5 h-2.5 rounded-full mr-2"
                                    style={{
                                      backgroundColor: point.serieColor,
                                    }}
                                  />
                                  <div className="text-sm font-medium text-light-300">
                                    {`${user.name}: `}
                                    <span className="ml-1 font-base text-light-0">
                                      {getTickLabel(
                                        point.data.yFormatted as number,
                                        selectedReportAnswer?.templateQuestion
                                      )}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  }}
                  curve="monotoneX"
                  enablePointLabel={true}
                  useMesh={true}
                />
              </div>
            </div>
            <div className="flex flex-col pr-4 rounded">
              <H4>{t("mads.visualizedEntries")}</H4>
              {selectedChartData?.data.map((d) => (
                <div className={`px-4 py-2`} key={d.id}>
                  <Checkbox
                    id={d.id}
                    label={
                      <div className="block p-1 -m-1">
                        <div className="relative flex items-center flex-1 min-w-0">
                          <div className="truncate">
                            <p className="pb-1 text-sm font-medium truncate text-dark-100">
                              {d.name}
                            </p>
                            <Badge
                              scale="sm"
                              variant={
                                mad.talent.id === d.id ? "secondary" : "warning"
                              }
                            >
                              {mad.talent.id === d.id
                                ? t("mads.talent")
                                : t("mads.referent", { count: 0 })}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    }
                    checked={!!selectedChartUsers?.find((su) => su.id === d.id)}
                    onChange={(e) => {
                      e.target.checked
                        ? setSelectedChartUsers((chartUsers) => [
                            ...chartUsers,
                            d,
                          ])
                        : setSelectedChartUsers((chartUsers) =>
                            [...chartUsers].filter((e) => e.id !== d.id)
                          );
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
    </div>
  );
};

export default React.memo(UserFollowUpAnalytics);
